import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { startCreatorConnectOnboarding } from "../services/settingsService";
import { setPageTitle } from "../components/DocumentTitle";
import "./CreatorOnboarding.css";

const SITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://100viewsonly.com";

function ProgressDots({ step }) {
  return (
    <div className="co-progress" aria-label={`Step ${step} of 2`}>
      <div className={`co-op-dot${step > 1 ? " done" : ""}${step === 1 ? " active" : ""}`} />
      <div className="co-op-line" />
      <div className={`co-op-dot${step === 2 ? " active" : ""}`} />
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function CreatorOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile, refreshUserProfile } = useAuth();

  const storedStep = Number(userProfile?.onboardingStep) || 1;
  const [step, setStep] = useState(storedStep >= 2 ? 2 : 1);

  const [creatorCategory, setCreatorCategory] = useState(userProfile?.creatorCategory || "");
  const [creatorGoal, setCreatorGoal] = useState(userProfile?.creatorGoal || "");
  const [contentStyle, setContentStyle] = useState(userProfile?.contentStyle || "");
  const [acceptLimitedDrops, setAcceptLimitedDrops] = useState(
    userProfile?.acceptLimitedDrops !== false
  );
  const [acceptCreatorPerks, setAcceptCreatorPerks] = useState(
    userProfile?.acceptCreatorPerks !== false
  );

  const [saving, setSaving] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const [error, setError] = useState("");

  const categories = [
    "Photography", "Fitness", "Gaming", "Lifestyle", "Fashion", "Music",
    "Art", "Comedy", "Adult", "Education", "Business", "Other",
  ];
  const goals = [
    "Build a community", "Monetize content", "Grow a brand",
    "Exclusive content", "Build supporters", "All of the above",
  ];
  const styles = [
    "Premium & cinematic", "Casual & personal", "Luxury aesthetic",
    "Minimalist", "Community focused", "High energy",
  ];

  useEffect(() => {
    setPageTitle("100ViewsOnly — Creator Setup");
  }, []);

  // Resume from Firestore when profile loads / updates
  useEffect(() => {
    if (!userProfile) return;
    if (userProfile.creatorCategory) setCreatorCategory(userProfile.creatorCategory);
    if (userProfile.creatorGoal) setCreatorGoal(userProfile.creatorGoal);
    if (userProfile.contentStyle) setContentStyle(userProfile.contentStyle);
    if (typeof userProfile.acceptLimitedDrops === "boolean") {
      setAcceptLimitedDrops(userProfile.acceptLimitedDrops);
    }
    if (typeof userProfile.acceptCreatorPerks === "boolean") {
      setAcceptCreatorPerks(userProfile.acceptCreatorPerks);
    }

    const s = Number(userProfile.onboardingStep) || 1;
    // Finished or skipped Connect step → leave onboarding
    if (s >= 3 || (userProfile.payoutSetupComplete && userProfile.creatorOnboardingComplete)) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (s >= 2) setStep(2);
    else setStep(1);
  }, [userProfile, navigate]);

  // Expired Account Link refresh
  useEffect(() => {
    if (searchParams.get("payout") === "refresh" && currentUser) {
      setStep(2);
      setError("Your Stripe setup link expired. Tap the button below to continue.");
    }
  }, [searchParams, currentUser]);

  const saveStep1AndAdvance = async () => {
    if (!currentUser) {
      setError("Please log in.");
      return;
    }
    if (!creatorCategory) {
      setError("Choose a creator category.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await updateDoc(doc(db, "users", currentUser.uid), {
        isCreator: true,
        creatorCategory,
        creatorGoal,
        contentStyle,
        acceptLimitedDrops,
        acceptCreatorPerks,
        onboardingStep: 2,
        creatorOnboardingComplete: true,
        updatedAt: serverTimestamp(),
      });

      if (refreshUserProfile) await refreshUserProfile(currentUser.uid);
      setStep(2);
    } catch (err) {
      console.error("Creator onboarding step 1 error:", err);
      setError("Could not save your setup. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const finishWithoutConnect = async () => {
    if (!currentUser) return;
    try {
      setConnectBusy(true);
      setError("");
      // Leave connectAccountId genuinely unset — only advance step marker
      await updateDoc(doc(db, "users", currentUser.uid), {
        onboardingStep: 3,
        creatorOnboardingComplete: true,
        updatedAt: serverTimestamp(),
      });
      if (refreshUserProfile) await refreshUserProfile(currentUser.uid);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Skip payout setup error:", err);
      setError("Could not continue. Try again.");
    } finally {
      setConnectBusy(false);
    }
  };

  const startStripeConnect = async () => {
    if (!currentUser) return;
    try {
      setConnectBusy(true);
      setError("");
      await updateDoc(doc(db, "users", currentUser.uid), {
        onboardingStep: 2,
        updatedAt: serverTimestamp(),
      });
      await startCreatorConnectOnboarding({
        returnUrl: `${SITE_ORIGIN}/dashboard?payout=return`,
        refreshUrl: `${SITE_ORIGIN}/creator-onboarding?payout=refresh`,
      });
    } catch (err) {
      console.error("Connect onboarding error:", err);
      setError(err?.message || "Could not start Stripe setup.");
      setConnectBusy(false);
    }
  };

  if (step === 2) {
    return (
      <div className="co-page">
        <div className="co-onboard-wrap">
          <ProgressDots step={2} />
          <div className="co-onboard-card">
            <div className="co-oc-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
            </div>
            <div className="co-oc-title">Let&apos;s get you paid</div>
            <div className="co-oc-sub">
              Set up your payout account so earnings from your drops land straight in your bank —
              takes about 5 minutes, handled securely by Stripe.
            </div>

            <div className="co-oc-perks">
              <div className="co-oc-perk">
                <CheckIcon />
                CAD, straight to your Canadian bank account
              </div>
              <div className="co-oc-perk">
                <CheckIcon />
                Your info is handled by Stripe — we never see your bank details
              </div>
              <div className="co-oc-perk">
                <CheckIcon />
                Paid out monthly, on the 1st
              </div>
            </div>

            {error && <div className="co-oc-error">{error}</div>}

            <button
              type="button"
              className="co-oc-btn-primary"
              onClick={startStripeConnect}
              disabled={connectBusy}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              {connectBusy ? "Opening Stripe…" : "Set Up Payouts with Stripe"}
            </button>
            <button
              type="button"
              className="co-oc-btn-skip"
              onClick={finishWithoutConnect}
              disabled={connectBusy}
            >
              Skip for now — I&apos;ll do this later
            </button>

            <div className="co-oc-note">
              You can start posting drops right away either way. We&apos;ll remind you once you&apos;ve
              earned something to collect.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page">
      <div className="co-step1-shell">
        <ProgressDots step={1} />

        <div className="studio-shell" style={{ padding: 0 }}>
          <div className="studio-header">
            <p className="landing-eyebrow">Creator Setup</p>
            <h1>Build your creator identity.</h1>
            <p className="subtitle">
              Tell supporters what you create, what your goals are, and how your creator page should feel.
            </p>
          </div>

          <div className="new-post-layout">
            <div className="new-post-form-card">
              <label className="form-label">Creator category</label>
              <div className="theme-selector-grid">
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`theme-choice-card ${
                      creatorCategory === category ? "theme-choice-active" : ""
                    }`}
                    onClick={() => setCreatorCategory(category)}
                  >
                    <h3>{category}</h3>
                  </button>
                ))}
              </div>

              <label className="form-label">Main creator goal</label>
              <select
                className="settings-select"
                value={creatorGoal}
                onChange={(e) => setCreatorGoal(e.target.value)}
              >
                <option value="">Select a goal</option>
                {goals.map((goal) => (
                  <option key={goal} value={goal}>{goal}</option>
                ))}
              </select>

              <label className="form-label">Content style</label>
              <select
                className="settings-select"
                value={contentStyle}
                onChange={(e) => setContentStyle(e.target.value)}
              >
                <option value="">Select a style</option>
                {styles.map((style) => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </select>

              <div className="settings-toggle-card">
                <div>
                  <p className="settings-label">Limited drops</p>
                  <p className="settings-description">
                    Allow limited-view moments and supporter unlocks.
                  </p>
                </div>
                <button
                  type="button"
                  className={`toggle-switch ${acceptLimitedDrops ? "toggle-switch-active" : ""}`}
                  onClick={() => setAcceptLimitedDrops((p) => !p)}
                >
                  <span />
                </button>
              </div>

              <div className="settings-toggle-card">
                <div>
                  <p className="settings-label">Creator perks</p>
                  <p className="settings-description">
                    Participate in user perks, unlocks, and supporter rewards.
                  </p>
                </div>
                <button
                  type="button"
                  className={`toggle-switch ${acceptCreatorPerks ? "toggle-switch-active" : ""}`}
                  onClick={() => setAcceptCreatorPerks((p) => !p)}
                >
                  <span />
                </button>
              </div>

              {error && <div className="auth-error">{error}</div>}

              <button
                type="button"
                className="support-button"
                disabled={saving}
                onClick={saveStep1AndAdvance}
              >
                {saving ? "Saving…" : "Continue to payouts"}
              </button>
            </div>

            <div className="new-post-preview-card">
              <p className="landing-eyebrow">Creator Preview</p>
              <h2>Your creator positioning</h2>
              <div className="info-card">
                <div className="milestone-badges-row">
                  {creatorCategory && (
                    <span className="badge-purple milestone-badge">✨ {creatorCategory}</span>
                  )}
                  {creatorGoal && (
                    <span className="badge-gold milestone-badge">🎯 {creatorGoal}</span>
                  )}
                  {contentStyle && (
                    <span className="badge-blue milestone-badge">🎨 {contentStyle}</span>
                  )}
                </div>
                <h3>
                  {userProfile?.displayName || userProfile?.username || "Creator"}
                </h3>
                <p className="settings-description">
                  Your creator page will combine premium visuals, limited moments, supporter perks,
                  scheduled drops, and direct audience interaction.
                </p>
                <div className="support-actions">
                  {acceptLimitedDrops && (
                    <span className="analytics-pill">🔒 Limited Drops</span>
                  )}
                  {acceptCreatorPerks && (
                    <span className="analytics-pill">✨ Creator Perks</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
